import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { branding } from '../constants/branding';
import { FaBolt, FaUniversity, FaQrcode, FaTimes, FaCamera, FaCreditCard } from 'react-icons/fa';
import { useAppSelector, useAppDispatch } from '../store/hooks';
import axiosInstance from '../services/axiosConfig';
import { apiService } from '../services/apiService';
import { featureFlags } from '../config/featureFlags';
import QrScanner from 'qr-scanner';
import type { ScanResult } from 'qr-scanner';
import { fetchPayees } from '../store/slices/payeeSlice';
import { setChildWallets, setLoading as setChildWalletLoading, setError as setChildWalletError } from '../store/slices/childWalletSlice';
import { fetchProfile } from '../store/slices/profileSlice';
import { getTrueLayerPaymentReturnUri } from '../utils/apiConfig';
import { runTrueLayerPaymentAuthorizationLoop } from '../utils/truelayerPaymentAuth';

// Payment methods
const paymentMethods = [
  { id: 'instant', label: 'SEPA Instant', fee: 1.00, icon: <FaBolt />, time: 'Instant', description: 'Send money instantly to any bank account' },
  { id: 'standard', label: 'SEPA Standard', fee: 0, icon: <FaUniversity />, time: '1-2 business days', description: 'Free transfer within 1-2 business days' },
  { id: 'internal', label: 'Internal', fee: 0, icon: <FaCreditCard />, time: 'Instant', description: 'Transfer between your own accounts instantly' }
];

const isLikelyIban = (value: string): boolean => {
  const compact = value.replace(/\s+/g, '').toUpperCase();
  return /^[A-Z]{2}\d{2}[A-Z0-9]{7,30}$/.test(compact);
};

/** IBAN sent to TrueLayer / displayed for payee — prefers explicit iban when API provides it */
const getPayeePaymentIban = (
  payee?: { iban?: string; accountNumber?: string; displayAccount?: string } | null
): string => {
  if (!payee) return '';
  const raw = (payee.iban || payee.accountNumber || payee.displayAccount || '').toString().trim();
  return raw;
};

const formatIbanForDisplay = (value: string): string => {
  const compact = value.replace(/\s+/g, '').toUpperCase();
  if (!compact) return '—';
  return compact.replace(/(.{4})/g, '$1 ').trim();
};

const buildShortPaymentReference = (prefix: string): string => {
  const cleanPrefix = (prefix || 'pay').replace(/[^a-zA-Z0-9]/g, '').slice(0, 3).toLowerCase() || 'pay';
  const ts = Date.now().toString(36).slice(-8);
  const rnd = Math.floor(Math.random() * 1296).toString(36).padStart(2, '0');
  return `${cleanPrefix}-${ts}-${rnd}`.slice(0, 18);
};

const MakePayment: React.FC = () => {
  const [amount, setAmount] = useState('');
  const [selectedPayee, setSelectedPayee] = useState<string | number | null>(null);
  const [note, setNote] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('standard');
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [otp, setOtp] = useState('');
  const [transactionList, setTransactionList] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<number | null>(null);
  /** String key so payee ids from API (number or string) always match the open detail modal */
  const [expandedRecipientKey, setExpandedRecipientKey] = useState<string | null>(null);
  
  // QR Scanner states
  const [showQrScanner, setShowQrScanner] = useState(false);
  const [qrScannerLoading, setQrScannerLoading] = useState(false);
  const [qrError, setQrError] = useState<string | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const codeReaderRef = useRef<QrScanner | null>(null);
  
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const dispatch = useAppDispatch();

  const MOBILE_BREAKPOINT = 768;
  const [isMobile, setIsMobile] = useState(window.innerWidth <= MOBILE_BREAKPOINT);

  // Redux state
  const token = useAppSelector((state) => state.auth.token);
  const user = useAppSelector((state) => state.auth.user);
  const isAuthenticated = useAppSelector((state) => state.auth.isAuthenticated);
  const authType = useAppSelector((state) => state.auth.type);
  
  // Access payees and child wallets from Redux
  const { payees, isLoading: payeesLoading } = useAppSelector((state) => state.payees);
  const { childWallets } = useAppSelector((state) => state.childWallet);

  // Add profile selector
  const profile = useAppSelector((state) => state.profile.profile);
  const profileLoading = useAppSelector((state) => state.profile.isLoading);
  const profileError = useAppSelector((state) => state.profile.error);
  const userRetailPermissions: string[] = (profile as { permissions?: string[] } | null)?.permissions || [];

  // Debug logs
  console.log('token:', token);
  console.log('profile:', profile);
  console.log('profileLoading:', profileLoading);
  console.log('profileError:', profileError);

  // Combine payees and child wallets into a unified recipients list
  const allRecipients = React.useMemo(() => {
    const recipients = [];
    
    // Add regular payees
    if (payees && payees.length > 0) {
      recipients.push(...payees.map(payee => ({
        ...payee,
        type: 'payee' as const,
        displayName: payee.name,
        displayAccount: payee.accountNumber,
        displayEmail: payee.email,
        displayAvatar: payee.avatar || `https://i.pravatar.cc/150?u=${payee.email}`,
        isChild: false
      })));
    }
    
    // Add child wallets
    if (childWallets && childWallets.length > 0) {
      recipients.push(...childWallets.map(child => ({
        id: `child-${child.childUserId}`,
        type: 'child' as const,
        displayName: `${child.firstName} ${child.lastName}`,
        displayAccount: child.primaryAccountNumber || child.accountNumber || child.childUserId.toString(),
        displayEmail: `${child.firstName?.toLowerCase()}.${child.lastName?.toLowerCase()}@childaccount.local`,
        displayAvatar: child.avatar || `https://i.pravatar.cc/150?u=${child.firstName}${child.lastName}`,
        isChild: true,
        childUserId: child.childUserId,
        name: `${child.firstName} ${child.lastName}`,
        accountNumber: child.primaryAccountNumber || child.accountNumber || child.childUserId.toString(),
        email: `${child.firstName?.toLowerCase()}.${child.lastName?.toLowerCase()}@childaccount.local`,
        avatar: child.avatar || `https://i.pravatar.cc/150?u=${child.firstName}${child.lastName}`,
        bank: 'Child Account',
        category: 'Family',
        lastPayment: 0, // Default value for child accounts
        lastPaymentDate: 'Never',
        totalPayments: 0,
        totalAmount: 0,
        isFavorite: false
      })));
    }
    
    return recipients;
  }, [payees, childWallets]);

  // Pre-select payee from URL parameter (when clicking Pay from Payees page)
  useEffect(() => {
    const payeeIdFromUrl = searchParams.get('payeeId');
    if (payeeIdFromUrl && allRecipients.length > 0) {
      // Try to find the payee by ID
      const payeeExists = allRecipients.find(r => String(r.id) === payeeIdFromUrl);
      if (payeeExists) {
        setSelectedPayee(payeeIdFromUrl);
        // Clear the URL parameter after setting it
        searchParams.delete('payeeId');
        navigate({ search: searchParams.toString() }, { replace: true });
      }
    }
  }, [searchParams, allRecipients, navigate]);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= MOBILE_BREAKPOINT);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Move fetchData to top-level so it can be called after payment
  const fetchData = useCallback(async () => {
    // For JWT auth, token is required. For session auth, token might be 'SESSION_AUTH' or null
    if (authType === 'JWT' && !token) {
      setError('Authentication token not available');
      setLoading(false);
      return;
    }
    if (!isAuthenticated) {
      setError('Not authenticated');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      console.log('Fetching payment data...');
      dispatch(fetchPayees());
      try {
        console.log('Fetching child wallets...');
        const childWalletsResponse = await axiosInstance.get('/child-accounts');
        console.log('Child wallets response:', childWalletsResponse);
        if (childWalletsResponse.status === 200) {
          const responseData = childWalletsResponse.data;
          console.log('Child wallets response data:', responseData);
          if (responseData.status === 'success' && responseData.data) {
            const wallets = responseData.data || [];
            console.log('Setting child wallets:', wallets);
            dispatch(setChildWallets(wallets));
          } else {
            console.error('Failed to fetch child wallets:', responseData.message);
            dispatch(setChildWalletError(responseData.message || 'Failed to fetch child wallets'));
          }
        }
      } catch (childWalletsErr: any) {
        console.error('Error fetching child wallets:', childWalletsErr);
        dispatch(setChildWalletError(childWalletsErr.message || 'Failed to fetch child wallets'));
      }
      // Fetch recent transactions
      const txRes = await axiosInstance.get('/payments/recent-transactions');
      console.log('Transactions response:', txRes);
      const txSuccess = txRes.data.success || txRes.data.status === 'success';
      if (!txSuccess) {
        throw new Error('Failed to fetch transaction data');
      }
      const txData = txRes.data.data;
      const transactions = Array.isArray(txData) ? txData : [];
      setTransactionList(transactions.map((tx: any) => ({
        id: tx.id,
        icon: tx.type === 'CREDIT' ? '💰' : '💸',
        title: tx.description,
        amount: tx.amount,
        date: new Date(tx.transactionDate).toLocaleDateString(),
        category: tx.category,
        status: tx.status,
        details: tx.description,
      })));
    } catch (err: any) {
      console.error('Error fetching payment data:', err);
      setError(err.message || 'Unknown error');
    } finally {
      setLoading(false);
    }
  }, [token, authType, isAuthenticated, dispatch]);

  // In useEffect, just call fetchData
  useEffect(() => {
    // Only fetch data if authenticated and (token is available for JWT or using session auth)
    if (isAuthenticated && (authType === 'SESSION' || (authType === 'JWT' && token))) {
      fetchData();
    }
  }, [fetchData, isAuthenticated, authType, token]);

  // Cleanup QR scanner on component unmount
  useEffect(() => {
    return () => {
      if (codeReaderRef.current) {
        try {
          codeReaderRef.current.stop();
          codeReaderRef.current.destroy();
        } catch (error) {
          console.warn('Error cleaning up QR scanner on unmount:', error);
        }
        codeReaderRef.current = null;
      }
    };
  }, []);

  // Initialize QR scanner when modal opens
  useEffect(() => {
    if (showQrScanner) {
      const timer = setTimeout(() => {
        initializeQrScanner();
      }, 200);
      
      return () => clearTimeout(timer);
    }
  }, [showQrScanner]);

  // Initialize QR scanner
  const initializeQrScanner = async () => {
    setQrScannerLoading(true);
    setQrError(null);
    
    try {
      if (!videoRef.current) {
        throw new Error('Video element not ready');
      }
      
      // Check if camera is available
      const hasCamera = await QrScanner.hasCamera();
      if (!hasCamera) {
        throw new Error('No camera devices found');
      }
      
      // Clean up any existing scanner first
      if (codeReaderRef.current) {
        try {
          await codeReaderRef.current.stop();
          codeReaderRef.current.destroy();
        } catch (cleanupError) {
          console.warn('Error cleaning up previous QR scanner:', cleanupError);
        }
        codeReaderRef.current = null;
      }
      
      // Create new scanner with error handling
      codeReaderRef.current = new QrScanner(
        videoRef.current,
        (result: ScanResult) => handleQrScanResult(result.data),
        {
          onDecodeError: (error: string | Error) => {
            // Only log actual errors, not "no QR code found" messages
            const errorMessage = typeof error === 'string' ? error : error.message;
            if (errorMessage && !errorMessage.includes('No QR code found')) {
              console.error('QR Scanner decode error:', error);
            }
          },
          highlightScanRegion: true,
          highlightCodeOutline: true,
        }
      );
      
      await codeReaderRef.current.start();
      setQrScannerLoading(false);
      
    } catch (error: any) {
      console.error('Failed to initialize QR scanner:', error);
      setQrError(error.message || 'Failed to access camera');
      setQrScannerLoading(false);
      
      // Clean up on error
      if (codeReaderRef.current) {
        try {
          codeReaderRef.current.destroy();
        } catch (cleanupError) {
          console.warn('Error cleaning up QR scanner on error:', cleanupError);
        }
        codeReaderRef.current = null;
      }
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedPayee || !amount) return;
    setShowConfirmation(true);
  };

  const handleExpand = (id: number) => setExpanded(expanded === id ? null : id);

  const handleConfirm = () => {
    setShowConfirmation(false);
    setShowOtpModal(true);
  };
  
  const handleVerifyOtp = async () => {
    if (!profile?.accountNumber) {
      setError('Your account information is not loaded. Please try again in a moment.');
      return;
    }
    
    if (!selectedPayeeData?.accountNumber) {
      setError('Please select a valid recipient.');
      return;
    }
    
    if (!amount || parseFloat(amount) <= 0) {
      setError('Please enter a valid amount.');
      return;
    }
    
    // Validate OTP is 6 digits
    if (otp.length !== 6) {
      alert('Please enter a 6-digit OTP code.');
      return;
    }
    
    setLoading(true);
    try {
      // Get the user's main account number from profile
      const fromAccountNumber = profile.accountNumber;
      // Get the selected payee's account number
      const toAccountNumber = selectedPayeeData.accountNumber;
      
      const payload = {
        fromAccountNumber: fromAccountNumber,
        toAccountNumber: toAccountNumber,
        amount: parseFloat(amount),
        description: note || '',
        paymentMethod: paymentMethod || 'standard',
        otpCode: otp // Send OTP code for Google Authenticator verification
      };
      
      console.log('Sending payment request:', payload);
      
      const response = await axiosInstance.post('/payments', payload);

      if (response.status === 200 && (response.data.success || response.data.status === 'success')) {
        setShowOtpModal(false);
        setShowSuccess(true);
        // Reset form fields
        setAmount('');
        setSelectedPayee(null);
        setNote('');
        setOtp('');
        fetchData(); // <-- Refresh transactions
      } else {
        setError(response.data.message || 'Payment failed');
        setOtp(''); // Clear OTP on error
      }
    } catch (error: any) {
      console.error('Payment error:', error);
      console.error('Payment error response:', error.response?.data);
      
      // Handle specific error responses from backend
      if (error.response?.status === 401) {
        alert('Invalid OTP. Please try again.');
      } else if (error.response?.status === 428) {
        alert('OTP verification required. Please enter your Google Authenticator code.');
      } else {
        // Show detailed error message
        const errorData = error.response?.data;
        let errorMessage = 'Payment failed. Please try again.';
        
        if (errorData) {
          if (errorData.errors && Array.isArray(errorData.errors) && errorData.errors.length > 0) {
            errorMessage = errorData.errors.join(', ');
          } else if (errorData.message) {
            errorMessage = errorData.message;
          }
        }
        
        setError(errorMessage);
        console.error('Payment error message:', errorMessage);
      }
      setOtp(''); // Clear OTP on error
    } finally {
      setLoading(false);
    }
  };

  const selectedPayeeData = (allRecipients || []).find(p => String(p.id) === selectedPayee);
  const selectedMethod = paymentMethods.find(m => m.id === paymentMethod);
  const totalAmount = parseFloat(amount || '0') + (selectedMethod?.fee || 0);

  const validatePayment = () => {
    if (!selectedPayee) {
      setError('Please select a recipient');
      return false;
    }
    if (!amount || parseFloat(amount) <= 0) {
      setError('Please enter a valid amount');
      return false;
    }
    if (!paymentMethod) {
      setError('Please select a payment method');
      return false;
    }
    return true;
  };

  const handleMakePayment = async () => {
    if (!validatePayment()) return;
    setShowConfirmation(true);
  };

  const handleQrScanResult = (qrText: string) => {
    try {
      console.log('QR Code scanned:', qrText);
      
      // Parse the JSON from QR code
      const qrData = JSON.parse(qrText);
      
      // Validate required fields
      if (!qrData.toAccountNumber || !qrData.amount) {
        throw new Error('Invalid QR code: Missing required fields');
      }
      
      // Define the payee type
      interface PayeeType {
        id: string | number;
        name: string;
        accountNumber: string;
        bankName?: string;
        isChild?: boolean;
      }
      
      // Enhanced payee matching - try multiple strategies
      let matchingPayee: PayeeType | null = null;
      let isChildAccount = false;
      
      // Strategy 1: Check if this is one of the parent's children first
      if (qrData.fromAccountId === "child-payment-request" && childWallets && childWallets.length > 0) {
        const matchingChild = childWallets.find(child => 
          child.primaryAccountNumber === qrData.toAccountNumber ||
          child.accountNumber === qrData.toAccountNumber ||
          child.childUserId.toString() === qrData.toAccountNumber
        );
        
        if (matchingChild) {
          // Create a payee-like object for the child
          matchingPayee = {
            id: `child-${matchingChild.childUserId}`,
            name: `${matchingChild.firstName} ${matchingChild.lastName}`,
            accountNumber: matchingChild.primaryAccountNumber || matchingChild.accountNumber || qrData.toAccountNumber,
            bankName: 'Your Child Account',
            isChild: true
          };
          isChildAccount = true;
          console.log('Found matching child account:', matchingChild);
        }
      }
      
      // Strategy 2: Exact account number match in payees
      if (!matchingPayee) {
        matchingPayee = allRecipients.find(payee => 
          payee.accountNumber === qrData.toAccountNumber
        ) || null;
      }
      
      // Strategy 3: Partial account number match (last 4 digits)
      if (!matchingPayee) {
        matchingPayee = allRecipients.find(payee => 
          payee.accountNumber && payee.accountNumber.endsWith(qrData.toAccountNumber.toString().slice(-4))
        ) || null;
      }
      
      // Strategy 4: If this is a child payment request, try to match by name in description
      if (!matchingPayee && qrData.fromAccountId === "child-payment-request") {
        // Extract child name from description if it follows the pattern "description (Request from ChildName)"
        const descriptionMatch = qrData.description.match(/\(Request from (.+)\)$/);
        if (descriptionMatch) {
          const childName = descriptionMatch[1];
          matchingPayee = allRecipients.find(payee => 
            payee.name && payee.name.toLowerCase().includes(childName.toLowerCase())
          ) || null;
        }
      }
      
      // Strategy 5: For development/testing - if no exact match found, show a more helpful error
      if (!matchingPayee) {
        const isChildRequest = qrData.fromAccountId === "child-payment-request";
        const errorMessage = isChildRequest 
          ? `Child payment request received, but child with account ${qrData.toAccountNumber} is not recognized. Please ensure this is your child's account or add them as a payee.`
          : `Recipient with account number ${qrData.toAccountNumber} not found in your payees list`;
        throw new Error(errorMessage);
      }
      
      // Auto-fill the form
      if (isChildAccount && matchingPayee) {
        // For child accounts, we don't need to modify the recipients list since they're already included
        setSelectedPayee(String(matchingPayee.id));
      } else if (matchingPayee) {
        setSelectedPayee(String(matchingPayee.id));
      }
      
      setAmount(qrData.amount.toString());
      if (qrData.description) {
        setNote(qrData.description);
      }
      
      // Close scanner and show success message
      stopQrScanner();
      
      // Show temporary success message with enhanced info for child requests
      setError(null);
      const isChildRequest = qrData.fromAccountId === "child-payment-request";
      const successMessage = isChildRequest
        ? `Child payment request received! ✅\n\nFrom: ${matchingPayee.name}\nAmount: ${branding.currency.symbol}${qrData.amount}\n\nForm has been auto-filled. Review and continue to send payment.`
        : `Payment form auto-filled from QR code!\nRecipient: ${matchingPayee.name}\nAmount: ${branding.currency.symbol}${qrData.amount}`;
      alert(successMessage);
      
    } catch (error: any) {
      console.error('Error processing QR code:', error);
      setQrError(error.message || 'Invalid QR code format');
      
      // Don't close scanner on error, let user try again
      setTimeout(() => {
        setQrError(null);
      }, 3000);
    }
  };

  const stopQrScanner = async () => {
    try {
      if (codeReaderRef.current) {
        // Stop the scanner first
        await codeReaderRef.current.stop();
        
        // Small delay to ensure stop is complete
        await new Promise(resolve => setTimeout(resolve, 100));
        
        // Destroy the scanner
        codeReaderRef.current.destroy();
        codeReaderRef.current = null;
      }
    } catch (error) {
      console.warn('Error stopping QR scanner:', error);
      // Force cleanup even if there's an error
      if (codeReaderRef.current) {
        try {
          codeReaderRef.current.destroy();
        } catch (destroyError) {
          console.warn('Error destroying QR scanner:', destroyError);
        }
        codeReaderRef.current = null;
      }
    } finally {
      // Always reset state
      setShowQrScanner(false);
      setQrScannerLoading(false);
      setQrError(null);
    }
  };

  // Fetch profile if missing (always retry when token changes and profile is missing)
  useEffect(() => {
    if (!profile && token && !profileLoading) {
      dispatch(fetchProfile());
    }
  }, [profile, token, dispatch, profileLoading]);

  const [accounts, setAccounts] = useState<any[]>([]);
  const [fromAccount, setFromAccount] = useState('');
  const [toAccount, setToAccount] = useState('');
  const [transferAmount, setTransferAmount] = useState('');
  const [transferDesc, setTransferDesc] = useState('');
  const [transferLoading, setTransferLoading] = useState(false);
  const [transferSuccess, setTransferSuccess] = useState(false);
  const [transferError, setTransferError] = useState('');
  const [showInternalModal, setShowInternalModal] = useState(false);
  const [isTrueLayerConnected, setIsTrueLayerConnected] = useState<boolean | null>(null);
  const [trueLayerBankStatus, setTrueLayerBankStatus] = useState<string | null>(null);
  const [isTrueLayerCreatingPayment, setIsTrueLayerCreatingPayment] = useState(false);
  const [isTrueLayerStartingAuthorization, setIsTrueLayerStartingAuthorization] = useState(false);
  const [trueLayerPaymentError, setTrueLayerPaymentError] = useState<string | null>(null);
  const [trueLayerPaymentStatus, setTrueLayerPaymentStatus] = useState<string | null>(null);
  const showTrueLayerPaymentsWidget =
    featureFlags.retail.enableTrueLayerRetail &&
    featureFlags.retail.enableTrueLayerPaymentsPageWidget &&
    userRetailPermissions.includes('VIEW_RETAIL_DASHBOARD_TRUELAYER');

  useEffect(() => {
    // Fetch user's accounts for internal transfer
    axiosInstance.get('/accounts').then(res => {
      setAccounts(res.data.data || []);
    });
  }, []);

  useEffect(() => {
    if (!showTrueLayerPaymentsWidget) return;
    const loadTrueLayerConnection = async () => {
      const response = await apiService.get<{ results?: Array<any> }>('/api/banking/truelayer/accounts');
      if (!response.success) {
        setIsTrueLayerConnected(false);
        setTrueLayerBankStatus(response.error || 'TrueLayer not connected. Connect from dashboard.');
        return;
      }
      const accounts = Array.isArray(response.data?.results) ? response.data.results : [];
      setIsTrueLayerConnected(accounts.length > 0);
      setTrueLayerBankStatus(
        accounts.length > 0
          ? `TrueLayer connected (${accounts.length} account${accounts.length > 1 ? 's' : ''})`
          : 'No linked TrueLayer accounts. Connect from dashboard.'
      );
    };
    loadTrueLayerConnection();
  }, [showTrueLayerPaymentsWidget]);

  const handleInternalTransfer = async () => {
    setTransferLoading(true);
    setTransferError('');
    setTransferSuccess(false);
    try {
      await axiosInstance.post('/accounts/transfer', {
        fromAccountNumber: fromAccount,
        toAccountNumber: toAccount,
        amount: parseFloat(transferAmount),
        description: transferDesc
      });
      setTransferSuccess(true);
      setTransferAmount('');
      setTransferDesc('');
      setFromAccount('');
      setToAccount('');
      // Optionally refresh balances/transactions here
      fetchData && fetchData();
      setShowInternalModal(false); // Close modal on success
    } catch (err: any) {
      setTransferError('Transfer failed: ' + (err.response?.data?.message || err.message));
    } finally {
      setTransferLoading(false);
    }
  };

  const handlePayWithTrueLayer = async () => {
    setTrueLayerPaymentError(null);
    setTrueLayerPaymentStatus(null);

    if (!showTrueLayerPaymentsWidget) {
      setTrueLayerPaymentError('TrueLayer payments are not enabled.');
      return;
    }
    if (!isTrueLayerConnected) {
      setTrueLayerPaymentError('Connect your bank account first from the dashboard.');
      return;
    }
    if (!selectedPayeeData) {
      setTrueLayerPaymentError('Please select a recipient.');
      return;
    }
    const beneficiaryIban = getPayeePaymentIban(selectedPayeeData);
    if (!beneficiaryIban) {
      setTrueLayerPaymentError('Recipient IBAN/account is required.');
      return;
    }
    const parsedAmount = Number(amount);
    if (!Number.isFinite(parsedAmount) || parsedAmount <= 0) {
      setTrueLayerPaymentError('Enter a valid amount greater than 0.');
      return;
    }

    if (!isLikelyIban(beneficiaryIban)) {
      setTrueLayerPaymentError('Selected recipient does not have a valid IBAN. Edit the payee and save a full IBAN (country code + check digits + account).');
      return;
    }

    const reference = buildShortPaymentReference('pay');
    setIsTrueLayerCreatingPayment(true);
    try {
      const createResponse = await apiService.post<any>('/api/banking/truelayer/payments', {
        amount: Number(parsedAmount.toFixed(2)),
        currency: branding.currency.code || 'EUR',
        reference,
        payment_method: {
          type: 'bank_transfer',
          provider_selection: { type: 'user_selected' },
          beneficiary: {
            type: 'external_account',
            reference,
            account_holder_name: selectedPayeeData.displayName || selectedPayeeData.name || 'Recipient',
            account_identifier: {
              type: 'iban',
              iban: beneficiaryIban,
            },
          },
        },
        user: {
          name: user ? `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.username : 'Retail User',
          email: user?.email || 'retail.user@zogzila.com',
        },
      });

      if (!createResponse.success) {
        setTrueLayerPaymentError(createResponse.error || 'Failed to create TrueLayer payment.');
        return;
      }

      const paymentId = String(createResponse.data?.id || '');
      const resourceToken = String(createResponse.data?.resource_token || '');
      if (!paymentId || !resourceToken) {
        setTrueLayerPaymentError('Payment created but missing authorization details. Try again.');
        return;
      }

      setTrueLayerPaymentStatus(`Payment created (${paymentId}). Starting authorization...`);
      setIsTrueLayerStartingAuthorization(true);
      const authResponse = await apiService.post<any>(
        `/api/banking/truelayer/payments/${paymentId}/authorization-flow`,
        {
          resource_token: resourceToken,
          provider_selection: { type: 'user_selected' },
          redirect: { return_uri: getTrueLayerPaymentReturnUri() },
        }
      );

      if (!authResponse.success) {
        setTrueLayerPaymentError(authResponse.error || 'Failed to start TrueLayer authorization.');
        return;
      }

      const loopResult = await runTrueLayerPaymentAuthorizationLoop(
        (path, body) => apiService.post<unknown>(path, body),
        paymentId,
        resourceToken,
        authResponse.data,
        (msg) => setTrueLayerPaymentStatus(msg),
        undefined,
        { beneficiaryIban: beneficiaryIban }
      );
      if (loopResult.status === 'error') {
        setTrueLayerPaymentError(loopResult.message);
      }
    } catch (err) {
      setTrueLayerPaymentError('TrueLayer payment failed. Please try again.');
    } finally {
      setIsTrueLayerCreatingPayment(false);
      setIsTrueLayerStartingAuthorization(false);
    }
  };

  // When opening the internal transfer modal, reset success and error messages
  const openInternalModal = () => {
    setShowInternalModal(true);
    setTransferSuccess(false);
    setTransferError('');
  };

  // --- SEPA LIMITS/USAGE LOGIC ---
  const isSepa = paymentMethod === 'instant' || paymentMethod === 'standard';
  const sepaLimits = profile ? {
    daily: Number(profile.sepaDailyLimit ?? 0),
    monthly: Number(profile.sepaMonthlyLimit ?? 0),
    yearly: Number(profile.sepaYearlyLimit ?? 0),
    single: Number(profile.sepaSingleTransactionLimit ?? 0),
    currency: profile.sepaLimitCurrency ?? 'EUR',
  } : { daily: 0, monthly: 0, yearly: 0, single: 0, currency: 'EUR' };
  const sepaUsage = profile ? {
    daily: Number(profile.sepaDailyUsage ?? 0),
    monthly: Number(profile.sepaMonthlyUsage ?? 0),
    yearly: Number(profile.sepaYearlyUsage ?? 0),
  } : { daily: 0, monthly: 0, yearly: 0 };
  const paymentAmount = parseFloat(amount || '0');
  const willExceedSingle = isSepa && paymentAmount > sepaLimits.single;
  const willExceedDaily = isSepa && (sepaUsage.daily + paymentAmount) > sepaLimits.daily;
  const willExceedMonthly = isSepa && (sepaUsage.monthly + paymentAmount) > sepaLimits.monthly;
  const willExceedYearly = isSepa && (sepaUsage.yearly + paymentAmount) > sepaLimits.yearly;
  const over80 = (limit: number, usage: number, amt: number) => limit > 0 && (usage + amt) / limit >= 0.8;
  const show80Daily = isSepa && over80(sepaLimits.daily, sepaUsage.daily, paymentAmount);
  const show80Monthly = isSepa && over80(sepaLimits.monthly, sepaUsage.monthly, paymentAmount);
  const show80Yearly = isSepa && over80(sepaLimits.yearly, sepaUsage.yearly, paymentAmount);

  return (
    <div style={{
      minHeight: '100vh',
      fontFamily: branding.fontFamily,
      background: branding.pageBackground,
      padding: isMobile ? '16px 0' : '32px 0',
      margin: 0
    }}>
      {/* Add CSS for QR scanner animations */}
      <style>
        {`
          @keyframes pulse {
            0% { opacity: 1; }
            50% { opacity: 0.5; }
            100% { opacity: 1; }
          }
        `}
      </style>
      
      {/* Header with Batch Payment button */}
      <div style={{
        maxWidth: 1200,
        margin: '0 auto',
        paddingBottom: 24,
        textAlign: 'left',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
      }}>
        <h1 style={{
          color: branding.textColor,
          fontWeight: 700,
          fontSize: isMobile ? 28 : 32,
          margin: 0,
          marginBottom: 8,
        }}>Payment</h1>
        <button
          onClick={() => navigate('/batch')}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            background: '#fff',
            border: 'none',
            borderRadius: 12,
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            padding: '8px 18px',
            fontWeight: 700,
            fontSize: 16,
            color: branding.textColor,
            cursor: 'pointer',
            transition: 'box-shadow 0.2s',
            marginLeft: 16,
          }}
        >
          <FaCreditCard style={{ color: '#FFD700', fontSize: 20 }} />
          Batch Payment
        </button>
      </div>

      {/* Show loading state */}
      {loading && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '50vh'
        }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 18, color: branding.textColor, marginBottom: 16 }}>Loading payment data...</div>
            <div style={{ color: '#666' }}>Please wait while we fetch your payment information</div>
          </div>
        </div>
      )}

      {/* Show error state */}
      {error && !loading && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '50vh'
        }}>
          <div style={{ textAlign: 'center', maxWidth: 400 }}>
            <div style={{ fontSize: 18, color: branding.errorColor, marginBottom: 16 }}>Error Loading Payment Data</div>
            <div style={{ color: '#666', marginBottom: 24 }}>{error}</div>
            <button 
              onClick={() => window.location.reload()} 
              style={{
                padding: '12px 24px',
                background: branding.primaryColor,
                color: '#fff',
                border: 'none',
                borderRadius: 12,
                fontSize: 16,
                fontWeight: 600,
                cursor: 'pointer'
              }}
            >
              Retry
            </button>
          </div>
        </div>
      )}

      {/* Main content - only show when not loading and no error */}
      {!loading && !error && (
        <>
          <div style={{
            maxWidth: 1200,
            margin: '0 auto',
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            gap: isMobile ? 0 : 20,
          }}>
            {/* Payee Selection (Left on desktop, top on mobile) */}
            <div style={{
              flex: isMobile ? 1 : '0 0 420px',
              maxWidth: isMobile ? '100%' : 420,
              minWidth: isMobile ? 0 : 320,
              background: '#fff',
              borderRadius: 20,
              boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
              padding: isMobile ? 20 : 32,
              marginBottom: isMobile ? 24 : 0,
              marginRight: isMobile ? 0 : 20,
            }}>
              <h2 style={{ color: branding.textColor, fontWeight: 700, fontSize: isMobile ? 20 : 24, marginBottom: 18 }}>Select Recipient</h2>
              
              {/* Show message if no payees */}
              {(!allRecipients || allRecipients.length === 0) ? (
                <div style={{ textAlign: 'center', padding: '40px 20px' }}>
                  <div style={{ fontSize: 16, color: '#666', marginBottom: 16 }}>No recipients found</div>
                  <div style={{ fontSize: 14, color: '#999' }}>Add recipients from the Payees page to make payments</div>
                </div>
              ) : (
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
                  gap: 12,
                  minWidth: 0,
                  width: '100%',
                }}>
                  {allRecipients.map(payee => (
                    <React.Fragment key={payee.id}>
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedPayee(String(payee.id));
                          setExpandedRecipientKey(String(payee.id));
                        }}
                        style={{
                          width: '100%',
                          minWidth: 0,
                          minHeight: 76,
                          maxWidth: '100%',
                          boxSizing: 'border-box',
                          padding: 12,
                          background: selectedPayee === String(payee.id) ? branding.primaryColor : '#f7fafd',
                          border: `2px solid ${selectedPayee === String(payee.id) ? branding.primaryColor : '#eee'}`,
                          borderRadius: 14,
                          cursor: 'pointer',
                          transition: 'all 0.2s',
                          textAlign: 'left',
                          boxShadow: selectedPayee === String(payee.id) ? '0 2px 8px rgba(10,132,255,0.10)' : '0 1px 4px rgba(0,0,0,0.03)',
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: 10,
                          flexDirection: 'row',
                          position: 'relative',
                          overflow: 'hidden',
                        }}
                      >
                        <img
                          src={payee.displayAvatar}
                          alt={payee.displayName}
                          style={{
                            width: 40,
                            height: 40,
                            borderRadius: '50%',
                            border: '2px solid #fff',
                            boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
                            flexShrink: 0,
                            marginTop: 2,
                          }}
                        />
                        <div style={{
                          display: 'flex',
                          flexDirection: 'column',
                          minWidth: 0,
                          flex: 1,
                          overflow: 'hidden',
                        }}>
                          <div style={{
                            fontWeight: 600,
                            fontSize: 14,
                            color: selectedPayee === String(payee.id) ? '#fff' : branding.textColor,
                            marginBottom: 4,
                            lineHeight: 1.2,
                            wordWrap: 'break-word',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}>
                            {payee.displayName}
                            {payee.isChild && (
                              <span style={{
                                fontSize: 10,
                                marginLeft: 6,
                                padding: '2px 6px',
                                background: selectedPayee === String(payee.id) ? 'rgba(255,255,255,0.2)' : branding.primaryColor,
                                color: '#fff',
                                borderRadius: 8,
                                display: 'inline-block',
                              }}>
                                Child
                              </span>
                            )}
                          </div>
                          <div style={{
                            color: selectedPayee === String(payee.id) ? 'rgba(255,255,255,0.8)' : '#666',
                            fontSize: 12,
                            lineHeight: 1.2,
                            marginBottom: 2,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}>***{payee.displayAccount.slice(-4)}</div>
                          <div style={{
                            color: selectedPayee === String(payee.id) ? 'rgba(255,255,255,0.7)' : '#999',
                            fontSize: 11,
                            lineHeight: 1.2,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}>{payee.displayEmail}</div>
                        </div>
                      </button>
                    </React.Fragment>
                  ))}
                </div>
              )}
              {expandedRecipientKey !== null && (() => {
                const payee = allRecipients.find(p => String(p.id) === expandedRecipientKey);
                if (!payee) return null;
                return (
                  <div
                    onClick={() => setExpandedRecipientKey(null)}
                    style={{
                      position: 'fixed',
                      top: 0,
                      left: 0,
                      width: '100vw',
                      height: '100vh',
                      background: 'rgba(0,0,0,0.3)',
                      zIndex: 2000,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <div
                      onClick={e => e.stopPropagation()}
                      style={{
                        background: '#fff',
                        borderRadius: 16,
                        boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
                        padding: 28,
                        minWidth: 320,
                        maxWidth: '90vw',
                        maxHeight: '80vh',
                        overflowY: 'auto',
                        position: 'relative',
                      }}
                    >
                      <button
                        onClick={() => setExpandedRecipientKey(null)}
                        style={{
                          position: 'absolute',
                          top: 12,
                          right: 12,
                          background: 'transparent',
                          border: 'none',
                          fontSize: 22,
                          cursor: 'pointer',
                          color: '#888',
                        }}
                        aria-label="Close"
                      >
                        ×
                      </button>
                      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 18 }}>
                        <img
                          src={payee.avatar || payee.displayAvatar}
                          alt={payee.name}
                          style={{ width: 56, height: 56, borderRadius: '50%', marginRight: 18 }}
                        />
                        <div>
                          <div style={{ fontWeight: 700, fontSize: 20, marginBottom: 4 }}>{payee.name}</div>
                          <div style={{ color: '#666', fontSize: 15 }}>
                            <div style={{ marginBottom: 6 }}>
                              <strong>IBAN (used for bank transfer):</strong>
                              <div
                                style={{
                                  fontFamily: 'ui-monospace, monospace',
                                  fontSize: 14,
                                  color: '#374151',
                                  marginTop: 4,
                                  wordBreak: 'break-all',
                                }}
                              >
                                {formatIbanForDisplay(getPayeePaymentIban(payee))}
                              </div>
                              {!isLikelyIban(getPayeePaymentIban(payee)) && (
                                <div
                                  style={{
                                    marginTop: 8,
                                    padding: '8px 10px',
                                    background: '#fffbeb',
                                    borderRadius: 8,
                                    border: '1px solid #fcd34d',
                                    color: '#92400e',
                                    fontSize: 13,
                                    lineHeight: 1.4,
                                  }}
                                >
                                  This does not look like a valid IBAN. TrueLayer requires a full IBAN (e.g.{' '}
                                  <span style={{ fontFamily: 'ui-monospace, monospace' }}>DE89370400440532013000</span>
                                  ). Update this payee on the Payees page with the correct IBAN.
                                </div>
                              )}
                            </div>
                            {'iban' in payee &&
                              payee.iban &&
                              payee.accountNumber &&
                              payee.iban.replace(/\s+/g, '').toUpperCase() !==
                                payee.accountNumber.replace(/\s+/g, '').toUpperCase() && (
                                <div style={{ marginBottom: 6 }}>
                                  <strong>Account reference (on file):</strong> {payee.accountNumber}
                                </div>
                              )}
                            <div><strong>Email:</strong> {payee.email || payee.displayEmail}</div>
                            {'lastPayment' in payee && payee.lastPayment !== undefined && !payee.isChild && (
                              <div><strong>Last Payment:</strong> {branding.currency.symbol}{payee.lastPayment.toFixed(2)}</div>
                            )}
                            {payee.isChild && (
                              <div><strong>Account Type:</strong> Child Account</div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })()}
            </div>
            {/* Payment Details + Transactions (Right on desktop, below on mobile) */}
            <div style={{
              flex: 2,
              display: 'flex',
              flexDirection: 'column',
              gap: 24,
              minWidth: 0,
            }}>
              {/* Payment Details Card */}
              <div style={{
                background: '#fff',
                borderRadius: 20,
                boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
                padding: isMobile ? 20 : 32,
                marginBottom: 0,
              }}>
                <h2 style={{ color: branding.textColor, fontWeight: 700, fontSize: isMobile ? 20 : 24, marginBottom: 18 }}>Payment Details</h2>
                
                {/* QR Scanner Button */}
                <div style={{ marginBottom: 20, textAlign: 'center' }}>
                  <button
                    type="button"
                    onClick={() => setShowQrScanner(true)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 8,
                      margin: '0 auto',
                      padding: '12px 24px',
                      background: branding.primaryColor,
                      color: '#fff',
                      border: 'none',
                      borderRadius: 12,
                      fontSize: 16,
                      fontWeight: 600,
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      boxShadow: '0 2px 8px rgba(10,132,255,0.12)',
                    }}
                  >
                    <FaQrcode size={18} />
                    Scan QR Code to Pay
                  </button>
                  <div style={{ fontSize: 12, color: '#666', marginTop: 8 }}>
                    Scan a payment QR code to auto-fill the form
                  </div>
                </div>
                
                {/* SEPA Limits & Usage Panel */}
                {isSepa && profile && (
                  <div style={{
                    background: '#f7fafd',
                    border: '1.5px solid #eee',
                    borderRadius: 14,
                    padding: 18,
                    marginBottom: 18,
                    marginTop: 8,
                    boxShadow: '0 2px 8px rgba(10,132,255,0.04)',
                    maxWidth: 480,
                  }}>
                    <div style={{ fontWeight: 700, color: branding.primaryColor, marginBottom: 8 }}>SEPA Transaction Limits</div>
                    <div style={{ fontSize: 14, color: '#444', marginBottom: 4 }}>Single: {sepaLimits.currency} {sepaLimits.single.toLocaleString()} | Daily: {sepaLimits.currency} {sepaLimits.daily.toLocaleString()} | Monthly: {sepaLimits.currency} {sepaLimits.monthly.toLocaleString()} | Yearly: {sepaLimits.currency} {sepaLimits.yearly.toLocaleString()}</div>
                    <div style={{ fontSize: 13, color: '#666' }}>
                      Usage: Single: {sepaLimits.currency} {paymentAmount.toLocaleString()} | Daily: {sepaLimits.currency} {(sepaUsage.daily + paymentAmount).toLocaleString()} | Monthly: {sepaLimits.currency} {(sepaUsage.monthly + paymentAmount).toLocaleString()} | Yearly: {sepaLimits.currency} {(sepaUsage.yearly + paymentAmount).toLocaleString()}
                    </div>
                    {(willExceedSingle || willExceedDaily || willExceedMonthly || willExceedYearly) && (
                      <div style={{ color: branding.errorColor, fontWeight: 600, marginTop: 8 }}>
                        {willExceedSingle && `Amount exceeds single transaction limit.`}
                        {willExceedDaily && ` Daily limit exceeded.`}
                        {willExceedMonthly && ` Monthly limit exceeded.`}
                        {willExceedYearly && ` Yearly limit exceeded.`}
                      </div>
                    )}
                    {(show80Daily || show80Monthly || show80Yearly) && !willExceedSingle && !willExceedDaily && !willExceedMonthly && !willExceedYearly && (
                      <div style={{ color: branding.warningColor || '#eab308', fontWeight: 600, marginTop: 8 }}>
                        {show80Daily && `Warning: You are above 80% of your daily SEPA limit. `}
                        {show80Monthly && `Warning: You are above 80% of your monthly SEPA limit. `}
                        {show80Yearly && `Warning: You are above 80% of your yearly SEPA limit.`}
                      </div>
                    )}
                  </div>
                )}

                <form onSubmit={handleSubmit} style={{ width: '100%' }}>
                  <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 24, marginBottom: 18 }}>
                    {/* Amount */}
                    <div style={{ flex: 1, minWidth: 180, position: 'relative' }}>
                      <div style={{
                        position: 'absolute',
                        left: 20,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        fontSize: 24,
                        fontWeight: 700,
                        color: branding.textColor,
                        pointerEvents: 'none',
                        zIndex: 1,
                      }}>{branding.currency.symbol}</div>
                      <input
                        type="number"
                        value={amount}
                        onChange={e => setAmount(e.target.value)}
                        placeholder="0.00"
                        style={{
                          width: '100%',
                          padding: '16px 20px 16px 50px',
                          fontSize: 24,
                          fontWeight: 700,
                          border: '1.5px solid #eee',
                          borderRadius: 14,
                          background: '#f7fafd',
                          color: branding.textColor,
                          outline: 'none',
                          boxSizing: 'border-box',
                          transition: 'border 0.2s',
                        }}
                      />
                    </div>
                    {/* Payment Method Icons */}
                    <div style={{ flex: 1, minWidth: 180, display: 'flex', flexDirection: 'column', alignItems: isMobile ? 'flex-start' : 'center', justifyContent: 'center' }}>
                      <div style={{ display: 'flex', gap: 24 }}>
                        {paymentMethods.filter(m => m.id === 'instant' || m.id === 'standard' || m.id === 'internal').map(method => (
                          <div key={method.id} style={{ textAlign: 'center' }}>
                            <button
                              type="button"
                              onClick={() => {
                                if (method.id === 'internal') {
                                  openInternalModal();
                                } else {
                                  setPaymentMethod(method.id);
                                }
                              }}
                              style={{
                                width: 56,
                                height: 56,
                                borderRadius: '50%',
                                border: paymentMethod === method.id ? `2.5px solid ${branding.primaryColor}` : '1.5px solid #eee',
                                background: paymentMethod === method.id ? '#f7fafd' : '#fff',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: paymentMethod === method.id ? branding.primaryColor : '#888',
                                cursor: 'pointer',
                                boxShadow: paymentMethod === method.id ? '0 2px 8px rgba(10,132,255,0.10)' : '0 1px 4px rgba(0,0,0,0.03)',
                                marginBottom: 8,
                                transition: 'all 0.2s',
                              }}
                            >
                              {React.isValidElement(method.icon)
                                ? React.cloneElement(method.icon as React.ReactElement<{ size?: number }>, { size: 24 })
                                : method.icon}
                            </button>
                            <span style={{ fontSize: 14, color: paymentMethod === method.id ? branding.primaryColor : '#888', fontWeight: paymentMethod === method.id ? 700 : 500 }}>
                              {method.label}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                  {/* Note and Submit */}
                  <div style={{ marginTop: 18 }}>
                    <textarea
                      value={note}
                      onChange={e => setNote(e.target.value)}
                      placeholder="Add a message for the recipient..."
                      style={{
                        width: '100%',
                        padding: 14,
                        borderRadius: 12,
                        border: '1.5px solid #eee',
                        background: '#f7fafd',
                        fontSize: 14,
                        minHeight: 80,
                        resize: 'vertical',
                        boxSizing: 'border-box',
                        fontFamily: branding.fontFamily,
                        marginBottom: 12,
                      }}
                    />
                    <button
                      type="submit"
                      disabled={!selectedPayee || !amount || !profile || profileLoading || willExceedSingle || willExceedDaily || willExceedMonthly || willExceedYearly}
                      style={{
                        width: '100%',
                        padding: '14px 0',
                        background: !selectedPayee || !amount || !profile || profileLoading || willExceedSingle || willExceedDaily || willExceedMonthly || willExceedYearly ? '#eee' : branding.primaryColor,
                        color: !selectedPayee || !amount || !profile || profileLoading || willExceedSingle || willExceedDaily || willExceedMonthly || willExceedYearly ? '#999' : '#fff',
                        border: 'none',
                        borderRadius: 12,
                        fontSize: 17,
                        fontWeight: 700,
                        cursor: !selectedPayee || !amount || !profile || profileLoading || willExceedSingle || willExceedDaily || willExceedMonthly || willExceedYearly ? 'not-allowed' : 'pointer',
                        transition: 'all 0.2s',
                        boxShadow: !selectedPayee || !amount || !profile || profileLoading || willExceedSingle || willExceedDaily || willExceedMonthly || willExceedYearly ? 'none' : '0 2px 8px rgba(10,132,255,0.12)',
                      }}
                    >
                      Continue to Review
                    </button>
                    {showTrueLayerPaymentsWidget && (
                      <button
                        type="button"
                        onClick={handlePayWithTrueLayer}
                        disabled={
                          !selectedPayee ||
                          !amount ||
                          !isTrueLayerConnected ||
                          isTrueLayerCreatingPayment ||
                          isTrueLayerStartingAuthorization
                        }
                        style={{
                          width: '100%',
                          marginTop: 10,
                          padding: '14px 0',
                          background: !selectedPayee || !amount || !isTrueLayerConnected || isTrueLayerCreatingPayment || isTrueLayerStartingAuthorization
                            ? '#eee'
                            : '#0dca9d',
                          color: !selectedPayee || !amount || !isTrueLayerConnected || isTrueLayerCreatingPayment || isTrueLayerStartingAuthorization
                            ? '#999'
                            : '#fff',
                          border: 'none',
                          borderRadius: 12,
                          fontSize: 16,
                          fontWeight: 700,
                          cursor: !selectedPayee || !amount || !isTrueLayerConnected || isTrueLayerCreatingPayment || isTrueLayerStartingAuthorization
                            ? 'not-allowed'
                            : 'pointer',
                        }}
                      >
                        {isTrueLayerCreatingPayment
                          ? 'Creating TrueLayer payment...'
                          : isTrueLayerStartingAuthorization
                            ? 'Starting bank authorization...'
                            : 'Pay with connected bank (TrueLayer)'}
                      </button>
                    )}
                    {showTrueLayerPaymentsWidget && trueLayerBankStatus && (
                      <div style={{ marginTop: 8, fontSize: 12, color: isTrueLayerConnected ? '#0dca9d' : '#6b7280' }}>
                        {trueLayerBankStatus}
                      </div>
                    )}
                    {showTrueLayerPaymentsWidget && trueLayerPaymentStatus && (
                      <div style={{ marginTop: 8, fontSize: 12, color: '#0dca9d' }}>
                        {trueLayerPaymentStatus}
                      </div>
                    )}
                    {showTrueLayerPaymentsWidget && trueLayerPaymentError && (
                      <div style={{ marginTop: 8, fontSize: 12, color: branding.errorColor }}>
                        {trueLayerPaymentError}
                      </div>
                    )}
                  </div>
                </form>
              </div>
              {/* Recent Transactions Card */}
              <div style={{
                background: '#fff',
                borderRadius: 20,
                boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
                padding: isMobile ? 20 : 32,
              }}>
                <h2 style={{ color: branding.textColor, fontWeight: 700, fontSize: isMobile ? 20 : 24, marginBottom: 20 }}>
                  Recent Transactions
                </h2>
                <div style={{ overflowX: 'auto', paddingBottom: 8 }}>
                  <table style={{ width: '100%', minWidth: isMobile ? 300 : 'auto', borderCollapse: 'collapse', fontSize: 16, marginBottom: 20 }}>
                    <tbody>
                      {(transactionList || []).length === 0 ? (
                        <tr><td colSpan={2} style={{ textAlign: 'center', color: '#888', padding: 32 }}>No recent transactions.</td></tr>
                      ) : (
                        (transactionList || []).slice(0, 5).map(tx => (
                          <React.Fragment key={tx.id}>
                            <tr
                              onClick={() => handleExpand(tx.id)}
                              style={{
                                background: 'transparent',
                                cursor: 'pointer',
                                transition: 'background 0.2s',
                              }}>
                              <td style={{ padding: '16px 12px', display: 'flex', alignItems: 'center', fontWeight: 600, borderBottom: expanded === tx.id ? 'none' : '1px solid #f0f0f0' }}>
                                <span style={{
                                  background: '#f2f2f7', borderRadius: '50%', width: 36, height: 36,
                                  display: 'flex', alignItems: 'center', justifyContent: 'center', marginRight: 12
                                }}>{tx.icon}</span>
                                {tx.title}
                              </td>
                              <td style={{ padding: '16px 12px', color: (tx.amount < 0 ? branding.errorColor : '#111'), fontWeight: 600, textAlign: 'right', borderBottom: expanded === tx.id ? 'none' : '1px solid #f0f0f0' }}>
                                {tx.amount < 0 ? '-' : '+'}{branding.currency.symbol}{Math.abs(tx.amount).toFixed(2)}
                              </td>
                            </tr>
                            {expanded === tx.id && (
                              <tr style={{ background: '#fafcff' }}>
                                <td colSpan={2} style={{ padding: '0 12px 12px' }}>
                                  <div style={{ padding: '16px', background: '#fff', borderRadius: 12, border: '1px solid #f0f0f0', marginTop: -1 }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10, fontSize: 14 }}>
                                      <span style={{ color: '#666' }}>Date:</span>
                                      <span style={{ fontWeight: 600 }}>{tx.date}</span>
                                    </div>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10, fontSize: 14 }}>
                                      <span style={{ color: '#666' }}>Category:</span>
                                      <span style={{ fontWeight: 600 }}>{tx.category}</span>
                                    </div>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10, fontSize: 14 }}>
                                       <span style={{ color: '#666' }}>Status:</span>
                                       <span style={{ fontWeight: 600, color: tx.status === 'Completed' ? branding.successColor : branding.errorColor }}>{tx.status}</span>
                                    </div>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14, alignItems: 'center' }}>
                                      <span style={{ color: '#666' }}>Details:</span>
                                      <span style={{ fontWeight: 600, textAlign: 'right', maxWidth: '70%' }}>{tx.details}</span>
                                    </div>
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
      {/* QR Scanner Modal */}
      {showQrScanner && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.9)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2000,
          padding: '24px',
        }}>
          <div style={{
            background: '#fff',
            borderRadius: 24,
            padding: isMobile ? 20 : 32,
            maxWidth: isMobile ? '90vw' : 480,
            width: '100%',
            maxHeight: '90vh',
            overflow: 'auto',
            boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
            textAlign: 'center',
            position: 'relative',
          }}>
            {/* Close Button */}
            <button
              onClick={() => stopQrScanner()}
              style={{
                position: 'absolute',
                top: 16,
                right: 16,
                background: 'rgba(0,0,0,0.1)',
                border: 'none',
                borderRadius: '50%',
                width: 40,
                height: 40,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                fontSize: 18,
                color: '#666',
              }}
            >
              <FaTimes />
            </button>
            
            <h3 style={{
              margin: 0,
              marginBottom: 16,
              color: branding.textColor,
              fontSize: isMobile ? 20 : 24,
              fontWeight: 700,
            }}>Scan QR Code</h3>
            
            <p style={{
              margin: 0,
              marginBottom: 24,
              color: '#666',
              fontSize: 14,
              lineHeight: 1.5,
            }}>
              Position the QR code within the camera frame to automatically fill the payment form
            </p>
            
            {/* Camera Preview */}
            <div style={{
              position: 'relative',
              width: '100%',
              maxWidth: 300,
              margin: '0 auto',
              borderRadius: 16,
              overflow: 'hidden',
              background: '#000',
              aspectRatio: '1',
            }}>
              <video
                ref={videoRef}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                }}
                playsInline
                muted
              />
              
              {/* Loading Overlay */}
              {qrScannerLoading && (
                <div style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: 'rgba(0,0,0,0.7)',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#fff',
                }}>
                  <div style={{ marginBottom: 16 }}>
                    <FaCamera size={32} />
                  </div>
                  <div style={{ fontSize: 16, fontWeight: 600 }}>Starting Camera...</div>
                  <div style={{ fontSize: 12, marginTop: 4 }}>Please allow camera access</div>
                </div>
              )}
              
              {/* Scanning Frame Overlay */}
              {!qrScannerLoading && !qrError && (
                <div style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  width: '80%',
                  height: '80%',
                  border: '2px solid #fff',
                  borderRadius: 8,
                  boxShadow: '0 0 0 9999px rgba(0,0,0,0.3)',
                  pointerEvents: 'none',
                }}>
                  <div style={{
                    position: 'absolute',
                    top: -2,
                    left: -2,
                    right: -2,
                    bottom: -2,
                    border: '2px solid ' + branding.primaryColor,
                    borderRadius: 8,
                    animation: 'pulse 2s infinite',
                  }} />
                </div>
              )}
            </div>
            
            {/* Error Message */}
            {qrError && (
              <div style={{
                marginTop: 16,
                padding: 12,
                background: '#fee',
                border: '1px solid #fcc',
                borderRadius: 8,
                color: branding.errorColor,
                fontSize: 14,
              }}>
                {qrError}
              </div>
            )}
            
            {/* Instructions */}
            <div style={{
              marginTop: 20,
              fontSize: 12,
              color: '#999',
              lineHeight: 1.4,
            }}>
              <div>• Hold your device steady</div>
              <div>• Ensure good lighting</div>
              <div>• QR code should fill most of the frame</div>
            </div>
          </div>
        </div>
      )}
      
      {/* Confirmation Modal, OTP Modal, Success Message (unchanged) */}
      {showConfirmation && selectedPayeeData && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.6)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '24px',
        }}>
          <div style={{
            background: '#fff',
            borderRadius: 24,
            padding: 40,
            maxWidth: 480,
            width: '100%',
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
            textAlign: 'center',
          }}>
            <div style={{
              width: 80,
              height: 80,
              borderRadius: '50%',
              background: branding.primaryColor,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 24px auto',
              fontSize: 32,
            }}>
              💸
            </div>
            <h3 style={{
              margin: 0,
              marginBottom: 16,
              color: branding.textColor,
              fontSize: 24,
              fontWeight: 700,
            }}>Confirm Payment</h3>
            <p style={{
              margin: 0,
              marginBottom: 32,
              color: '#666',
              fontSize: 16,
              lineHeight: 1.5,
            }}>
              You are about to send <strong style={{ color: branding.primaryColor }}>{branding.currency.symbol}{totalAmount.toFixed(2)}</strong> to <strong>{selectedPayeeData.displayName || selectedPayeeData.name}</strong>
              {selectedPayeeData.isChild && (
                <span style={{
                  display: 'block',
                  fontSize: 14,
                  color: '#888',
                  marginTop: 4,
                }}>
                  (Child Account)
                </span>
              )}
            </p>
            {getPayeePaymentIban(selectedPayeeData) ? (
              <p
                style={{
                  margin: '0 0 20px 0',
                  color: '#666',
                  fontSize: 14,
                  lineHeight: 1.5,
                  textAlign: 'left',
                }}
              >
                <span style={{ color: '#888' }}>Recipient IBAN: </span>
                <span style={{ fontFamily: 'ui-monospace, monospace', color: '#374151', wordBreak: 'break-all' }}>
                  {formatIbanForDisplay(getPayeePaymentIban(selectedPayeeData))}
                </span>
              </p>
            ) : null}

            <div style={{
              background: '#f7fafd',
              borderRadius: 16,
              padding: 20,
              marginBottom: 32,
              textAlign: 'left',
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                <span style={{ color: '#666' }}>Amount:</span>
                <span style={{ fontWeight: 600 }}>{branding.currency.symbol}{parseFloat(amount).toFixed(2)}</span>
              </div>
              {selectedMethod?.fee ? (
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                  <span style={{ color: '#666' }}>Fee:</span>
                  <span style={{ fontWeight: 600, color: branding.errorColor }}>{branding.currency.symbol}{selectedMethod.fee.toFixed(2)}</span>
                </div>
              ) : (
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                  <span style={{ color: '#666' }}>Fee:</span>
                  <span style={{ fontWeight: 600, color: branding.successColor }}>Free</span>
                </div>
              )}
              <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: 12, borderTop: '1px solid #eee' }}>
                <span style={{ fontWeight: 600 }}>Total:</span>
                <span style={{ fontWeight: 700, color: branding.primaryColor, fontSize: 18 }}>{branding.currency.symbol}{totalAmount.toFixed(2)}</span>
              </div>
            </div>

            <div style={{ display: 'flex', gap: 16 }}>
              <button
                onClick={() => setShowConfirmation(false)}
                style={{
                  flex: 1,
                  padding: '16px 0',
                  background: '#fff',
                  border: '1.5px solid #eee',
                  borderRadius: 12,
                  fontSize: 16,
                  fontWeight: 600,
                  color: '#666',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleConfirm}
                style={{
                  flex: 1,
                  padding: '16px 0',
                  background: branding.primaryColor,
                  border: 'none',
                  borderRadius: 12,
                  fontSize: 16,
                  fontWeight: 600,
                  color: '#fff',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  boxShadow: '0 4px 16px rgba(10,132,255,0.2)',
                }}
              >
                Confirm Payment
              </button>
            </div>
          </div>
        </div>
      )}

      {showOtpModal && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center',
          justifyContent: 'center', zIndex: 1010, padding: 24,
        }}>
          <div style={{
            background: '#fff', borderRadius: 24, padding: isMobile ? 24 : 40,
            maxWidth: 480, width: '100%', boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
            textAlign: 'center',
          }}>
            <h3 style={{
              margin: 0, marginBottom: 16, color: branding.textColor,
              fontSize: isMobile ? 20 : 24, fontWeight: 700,
            }}>Google Authenticator Required</h3>
            <p style={{ margin: '0 auto 32px auto', color: '#666', maxWidth: 400 }}>
              Enter the 6-digit code from your Google Authenticator app to authorize this payment.
            </p>
            <input
              type="tel"
              value={otp}
              onChange={(e) => setOtp(e.target.value.replace(/[^0-9]/g, ''))}
              maxLength={6}
              style={{
                width: '100%',
                padding: '16px 0',
                fontSize: 32,
                fontWeight: 700,
                textAlign: 'center',
                letterSpacing: isMobile ? 8 : 16,
                border: '2px solid #eee',
                borderRadius: 12,
                marginBottom: 32,
                color: branding.textColor,
                boxSizing: 'border-box',
              }}
            />
            <div style={{ display: 'flex', gap: 16 }}>
              <button
                onClick={() => setShowOtpModal(false)}
                style={{
                  flex: 1, padding: '16px 0', background: '#fff',
                  border: '1.5px solid #eee', borderRadius: 12, fontSize: 16,
                  fontWeight: 600, color: '#666', cursor: 'pointer',
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleVerifyOtp}
                disabled={!profile || profileLoading}
                style={{
                  flex: 1, padding: '16px 0', background: !profile || profileLoading ? '#eee' : branding.primaryColor,
                  border: 'none', borderRadius: 12, fontSize: 16,
                  fontWeight: 600, color: !profile || profileLoading ? '#999' : '#fff', cursor: !profile || profileLoading ? 'not-allowed' : 'pointer',
                }}
              >
                Verify & Pay
              </button>
            </div>
          </div>
        </div>
      )}

      {showSuccess && (
        <div style={{
          position: 'fixed',
          top: 24,
          right: 24,
          background: branding.successColor,
          color: '#fff',
          padding: '16px 24px',
          borderRadius: 12,
          boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
          zIndex: 1001,
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          animation: 'slideIn 0.3s ease-out',
        }}>
          <span style={{ fontSize: 20 }}>✅</span>
          <div>
            <div style={{ fontWeight: 600, marginBottom: 2 }}>Payment Successful!</div>
            <div style={{ fontSize: 14, opacity: 0.9 }}>Your payment has been processed</div>
          </div>
        </div>
      )}

      {/* Loading state for profile */}
      {(profileLoading || !profile) && !error && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '50vh'
        }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 18, color: branding.textColor, marginBottom: 16 }}>Loading your account information...</div>
            <div style={{ color: '#666' }}>Please wait while we load your profile</div>
          </div>
        </div>
      )}

      {/* Show profileError in the UI if present */}
      {profileError && !loading && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '50vh'
        }}>
          <div style={{ textAlign: 'center', maxWidth: 400 }}>
            <div style={{ fontSize: 18, color: branding.errorColor, marginBottom: 16 }}>Error Loading Profile</div>
            <div style={{ color: '#666', marginBottom: 24 }}>{profileError}</div>
            <button 
              onClick={() => dispatch(fetchProfile())} 
              style={{
                padding: '12px 24px',
                background: branding.primaryColor,
                color: '#fff',
                border: 'none',
                borderRadius: 12,
                fontSize: 16,
                fontWeight: 600,
                cursor: 'pointer'
              }}
            >
              Retry
            </button>
          </div>
        </div>
      )}

      {/* Internal Transfer Modal */}
      {showInternalModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.6)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 3000,
        }}>
          <div style={{
            background: '#fff',
            borderRadius: 20,
            boxShadow: '0 4px 24px rgba(0,0,0,0.12)',
            padding: 32,
            minWidth: 340,
            maxWidth: '90vw',
            position: 'relative',
          }}>
            <button onClick={() => setShowInternalModal(false)} style={{ position: 'absolute', top: 16, right: 16, background: 'none', border: 'none', fontSize: 24, cursor: 'pointer', color: '#888' }}>×</button>
            <h2 style={{ color: branding.textColor, fontWeight: 700, fontSize: 22, marginBottom: 18 }}>Internal Transfer (Checking ↔ Savings)</h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16, maxWidth: 400 }}>
              <select value={fromAccount} onChange={e => setFromAccount(e.target.value)} style={{ padding: 12, borderRadius: 8, border: '1.5px solid #eee' }}>
                <option value="">From Account</option>
                {accounts.filter(a => a.type === 'CHECKING' || a.type === 'SAVINGS').map(a =>
                  <option key={a.iban} value={a.iban}>
                    {a.type} (****{a.iban.slice(-4)}) - {a.balance} {a.currency}
                  </option>
                )}
              </select>
              <select value={toAccount} onChange={e => setToAccount(e.target.value)} style={{ padding: 12, borderRadius: 8, border: '1.5px solid #eee' }}>
                <option value="">To Account</option>
                {accounts.filter(a => (a.type === 'CHECKING' || a.type === 'SAVINGS') && a.iban !== fromAccount).map(a =>
                  <option key={a.iban} value={a.iban}>
                    {a.type} (****{a.iban.slice(-4)}) - {a.balance} {a.currency}
                  </option>
                )}
              </select>
              <input type="number" value={transferAmount} onChange={e => setTransferAmount(e.target.value)} placeholder="Amount" style={{ padding: 12, borderRadius: 8, border: '1.5px solid #eee' }} />
              <input type="text" value={transferDesc} onChange={e => setTransferDesc(e.target.value)} placeholder="Description (optional)" style={{ padding: 12, borderRadius: 8, border: '1.5px solid #eee' }} />
              <button onClick={handleInternalTransfer} disabled={!fromAccount || !toAccount || !transferAmount || transferLoading} style={{
                padding: '14px 0',
                background: !fromAccount || !toAccount || !transferAmount || transferLoading ? '#eee' : branding.primaryColor,
                color: !fromAccount || !toAccount || !transferAmount || transferLoading ? '#999' : '#fff',
                border: 'none',
                borderRadius: 12,
                fontSize: 17,
                fontWeight: 700,
                cursor: !fromAccount || !toAccount || !transferAmount || transferLoading ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s',
                boxShadow: !fromAccount || !toAccount || !transferAmount || transferLoading ? 'none' : '0 2px 8px rgba(10,132,255,0.12)',
              }}>
                {transferLoading ? 'Transferring...' : 'Transfer'}
              </button>
              {transferSuccess && <div style={{ color: branding.successColor, marginTop: 8 }}>Transfer successful!</div>}
              {transferError && <div style={{ color: branding.errorColor, marginTop: 8 }}>{transferError}</div>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MakePayment; 